import {
  useInfiniteQuery,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { VISITS_AUTO_FETCH_MAX_PAGES } from '../config/constants'
import { AGENT_URL } from '../config/env'
import { visitKeys } from '../lib/queryKeys'
import { ApiError } from '../lib/api'
import type { VisitRecord } from '../features/visits/types'
import type { FilterCondition } from '../utils/filterUtils'
import { drainCursorPages } from '../lib/drainCursorPages'

export interface VisitsQueryParams {
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  // Structured filters serialized as filter[field][operator]=value (parsed +
  // validated server-side by relay's /data/visits).
  filters?: FilterCondition[]
}

export interface VisitsResponse {
  data: VisitRecord[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
    total: number | null
  }
}

async function fetchVisits(
  token: string,
  params: VisitsQueryParams,
  cursor?: string
): Promise<VisitsResponse> {
  const search = new URLSearchParams()
  if (params.limit) {
    search.set('limit', params.limit.toString())
  }
  if (cursor) {
    search.set('cursor', cursor)
  }
  if (params.sort_by) {
    search.set('sort_by', params.sort_by)
  }
  if (params.sort_order) {
    search.set('sort_order', params.sort_order)
  }
  if (params.filters && params.filters.length > 0) {
    for (const filter of params.filters) {
      const value = filter.value.trim()
      if (!value) continue
      search.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }

  const url = `${AGENT_URL}/data/visits?${search.toString()}`

  const response = await fetch(url, {
    headers: {
      // Match the calls/patients/appointments endpoints: pass Clerk JWT directly
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch visits: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      // relay validation errors surface as `detail`; hoku's own errors as `error`.
      errorMessage = errorData.detail || errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new ApiError(errorMessage, response.status)
  }

  const raw: any = await response.json()
  const rawVisits: any[] = Array.isArray(raw?.data) ? raw.data : []

  const data: VisitRecord[] = rawVisits.map((visit: any): VisitRecord => {
    return {
      id: String(visit.id ?? ''),
      created_at:
        typeof visit.created_at === 'string' ? visit.created_at : null,
      updated_at:
        typeof visit.updated_at === 'string' ? visit.updated_at : null,

      org_id: typeof visit.org_id === 'string' ? visit.org_id : null,
      org_slug: typeof visit.org_slug === 'string' ? visit.org_slug : null,
      patient_id:
        typeof visit.patient_id === 'string' ? visit.patient_id : null,
      provider_id:
        typeof visit.provider_id === 'string' ? visit.provider_id : null,

      visit_date:
        typeof visit.visit_date === 'string' ? visit.visit_date : null,
      visit_type:
        typeof visit.visit_type === 'string' ? visit.visit_type : null,
      visit_groups:
        typeof visit.visit_groups === 'string' ? visit.visit_groups : null,
      program: typeof visit.program === 'string' ? visit.program : null,
      visit_sets:
        typeof visit.visit_sets === 'string' ? visit.visit_sets : null,
      location: typeof visit.location === 'string' ? visit.location : null,

      is_uds_visit:
        typeof visit.is_uds_visit === 'boolean' ? visit.is_uds_visit : null,
      is_uds_medical_visit:
        typeof visit.is_uds_medical_visit === 'boolean'
          ? visit.is_uds_medical_visit
          : null,
      is_billable:
        typeof visit.is_billable === 'boolean' ? visit.is_billable : null,
      billing_codes:
        typeof visit.billing_codes === 'string' ? visit.billing_codes : null,
      diagnosis_codes:
        typeof visit.diagnosis_codes === 'string'
          ? visit.diagnosis_codes
          : null,
      primary_payer_group:
        typeof visit.primary_payer_group === 'string'
          ? visit.primary_payer_group
          : null,
      primary_payer:
        typeof visit.primary_payer === 'string' ? visit.primary_payer : null,

      source_name:
        typeof visit.source_name === 'string' ? visit.source_name : null,
      source_visit_id:
        typeof visit.source_visit_id === 'string'
          ? visit.source_visit_id
          : null,
      source_patient_id:
        typeof visit.source_patient_id === 'string'
          ? visit.source_patient_id
          : null,
    }
  })

  const rawPagination = raw?.pagination ?? {}
  const limit =
    typeof rawPagination.limit === 'number'
      ? rawPagination.limit
      : (params.limit ?? 25)

  const has_more = Boolean(rawPagination.has_more)
  const next_cursor =
    typeof rawPagination.next_cursor === 'string'
      ? (rawPagination.next_cursor as string)
      : null

  const total =
    typeof rawPagination.total === 'number' ? rawPagination.total : null

  return {
    data,
    pagination: {
      limit,
      has_more,
      next_cursor,
      total,
    },
  }
}

export function useVisitsQuery(params: VisitsQueryParams = {}) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: ['visits', organization?.id, params],
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchVisits(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    // Keep prior rows visible during a filtered refetch so the table doesn't
    // flash its empty ("no results") state while the request resolves.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export interface VisitsRangeResult {
  visits: VisitRecord[]
  /** True when the page cap stopped the fetch before the cursor drained. */
  truncated: boolean
}

/**
 * Fetch a bounded visits result set completely, every page walked inside one
 * queryFn. Mirrors useAppointmentsRangeQuery: one loading state and one error
 * path, with `truncated` set when the page cap is hit. Used for a single
 * client's history, where a count and a care team need every row.
 */
export function useVisitsRangeQuery(
  params: VisitsQueryParams = {},
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: visitKeys.range(organization?.id, params),
    enabled: !!organization?.id && (options.enabled ?? true),
    queryFn: async (): Promise<VisitsRangeResult> => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      const { rows, truncated } = await drainCursorPages(
        (cursor) => fetchVisits(token, params, cursor),
        VISITS_AUTO_FETCH_MAX_PAGES
      )
      return { visits: rows, truncated }
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
